/**
 * Home-v2 modular Firebase: app + Firestore + Analytics + Remote Config.
 * No Auth / Storage. Exposes window.firebase compat for legacy home scripts.
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit as fsLimit,
  startAfter,
  serverTimestamp,
  Timestamp,
  type DocumentData,
  type QueryConstraint,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { logEvent, type Analytics } from 'firebase/analytics'
import {
  fetchAndActivate,
  getValue,
  type RemoteConfig,
} from 'firebase/remote-config'
import {
  exposeBenjarukHomeFirebase,
  getApp,
  getDb,
  getFirebaseAnalytics,
  getFirebaseRemoteConfig,
  getReady,
} from './core'

type WhereOp =
  | '<'
  | '<='
  | '=='
  | '!='
  | '>='
  | '>'
  | 'array-contains'
  | 'in'
  | 'array-contains-any'
  | 'not-in'

declare global {
  interface Window {
    firebaseRemoteConfigData?: {
      show_coupon: boolean
      show_random_products: boolean
      home_message_variant: string
    }
    RemoteConfigManager?: {
      applyRemoteData?: (data: Record<string, unknown>, source?: string) => void
      init?: () => Promise<unknown>
    }
  }
}

function refFromPath(path: string) {
  const parts = path.split('/').filter(Boolean)
  if (parts.length % 2 === 0) {
    return doc(getDb(), ...(parts as [string, ...string[]]))
  }
  throw new Error('[home-v2] invalid doc path: ' + path)
}

function colFromPath(path: string) {
  const parts = path.split('/').filter(Boolean)
  if (parts.length % 2 === 1) {
    return collection(getDb(), ...(parts as [string, ...string[]]))
  }
  throw new Error('[home-v2] invalid collection path: ' + path)
}

function snapToCompat(snap: QueryDocumentSnapshot | Awaited<ReturnType<typeof getDoc>>) {
  const exists =
    typeof (snap as { exists: boolean | (() => boolean) }).exists === 'function'
      ? (snap as { exists: () => boolean }).exists()
      : Boolean((snap as { exists: boolean }).exists)

  return {
    exists,
    id: snap.id,
    ref: {
      id: snap.id,
      path: snap.ref.path,
      set: (data: DocumentData, opts?: { merge?: boolean }) =>
        setDoc(snap.ref, data, opts?.merge ? { merge: true } : undefined),
      update: (data: DocumentData) => updateDoc(snap.ref, data),
      get: async () => snapToCompat(await getDoc(snap.ref)),
      collection: (name: string) => makeCollection(snap.ref.path + '/' + name),
      delete: () => deleteDoc(snap.ref),
    },
    data: () => (exists ? snap.data() : undefined),
  }
}

function makeQuery(path: string, constraints: QueryConstraint[]) {
  const col = colFromPath(path)
  const q = constraints.length ? query(col, ...constraints) : query(col)
  return {
    where(field: string, op: WhereOp, value: unknown) {
      return makeQuery(path, [...constraints, where(field, op, value)])
    },
    orderBy(field: string, dir: 'asc' | 'desc' = 'asc') {
      return makeQuery(path, [...constraints, orderBy(field, dir)])
    },
    limit(n: number) {
      return makeQuery(path, [...constraints, fsLimit(n)])
    },
    startAfter(cursor: QueryDocumentSnapshot | unknown) {
      return makeQuery(path, [...constraints, startAfter(cursor as QueryDocumentSnapshot)])
    },
    async get() {
      const res = await getDocs(q)
      return {
        empty: res.empty,
        size: res.size,
        docs: res.docs.map((d) => snapToCompat(d)),
        forEach: (cb: (d: ReturnType<typeof snapToCompat>) => void) => {
          res.docs.forEach((d) => cb(snapToCompat(d)))
        },
      }
    },
  }
}

function makeDoc(path: string) {
  const d = refFromPath(path)
  return {
    id: d.id,
    path: d.path,
    collection(name: string) {
      return makeCollection(path + '/' + name)
    },
    async get() {
      return snapToCompat(await getDoc(d))
    },
    set(data: DocumentData, opts?: { merge?: boolean }) {
      return setDoc(d, data, opts?.merge ? { merge: true } : undefined)
    },
    update(data: DocumentData) {
      return updateDoc(d, data)
    },
    delete() {
      return deleteDoc(d)
    },
  }
}

function makeCollection(path: string) {
  return {
    path,
    doc(id?: string) {
      if (id === undefined || id === null || id === '') {
        const auto = doc(colFromPath(path))
        return makeDoc(auto.path)
      }
      return makeDoc(path + '/' + id)
    },
    where(field: string, op: WhereOp, value: unknown) {
      return makeQuery(path, [where(field, op, value)])
    },
    orderBy(field: string, dir: 'asc' | 'desc' = 'asc') {
      return makeQuery(path, [orderBy(field, dir)])
    },
    limit(n: number) {
      return makeQuery(path, [fsLimit(n)])
    },
    async get() {
      return makeQuery(path, []).get()
    },
    add(data: DocumentData) {
      const auto = doc(colFromPath(path))
      return setDoc(auto, data).then(() => makeDoc(auto.path))
    },
  }
}

function makeFirestoreService() {
  return Object.assign(
    function firestore() {
      return {
        collection(name: string) {
          return makeCollection(name)
        },
        doc(path: string) {
          return makeDoc(path)
        },
        FieldValue: { serverTimestamp },
        Timestamp,
      }
    },
    {
      FieldValue: { serverTimestamp },
      Timestamp,
    },
  )
}

function makeAnalyticsService() {
  return function analytics() {
    return {
      async logEvent(name: string, params?: Record<string, unknown>) {
        const ga = await getFirebaseAnalytics()
        if (ga) logEvent(ga, name, params)
      },
    }
  }
}

function normalizeHomeMessageVariant(value: string): string {
  const normalized = (value || 'control').toString().trim().toLowerCase()
  if (normalized === 'new_copy' || normalized === 'b') return 'new_copy'
  return 'control'
}

function makeRemoteConfigService() {
  return function remoteConfig() {
    const rc = getFirebaseRemoteConfig()
    return {
      get settings() {
        return rc.settings
      },
      set settings(v: RemoteConfig['settings']) {
        rc.settings = v
      },
      get defaultConfig() {
        return rc.defaultConfig
      },
      set defaultConfig(v: RemoteConfig['defaultConfig']) {
        rc.defaultConfig = v
      },
      fetchAndActivate() {
        return fetchAndActivate(rc)
      },
      getValue(key: string) {
        const v = getValue(rc, key)
        return {
          asBoolean: () => v.asBoolean(),
          asString: () => v.asString(),
          asNumber: () => v.asNumber(),
          getSource: () => v.getSource(),
        }
      },
    }
  }
}

async function fetchHomeRemoteConfig(): Promise<void> {
  const rc = getFirebaseRemoteConfig()
  try {
    await fetchAndActivate(rc)
  } catch (e) {
    console.error('[home-v2] remote config fetch failed', e)
  }

  const showCoupon = getValue(rc, 'show_coupon').asBoolean()
  const showRandomProducts = getValue(rc, 'show_random_products').asBoolean()
  const homeMessageVariant = normalizeHomeMessageVariant(
    getValue(rc, 'home_message_variant').asString() || 'control',
  )

  const data = {
    show_coupon: showCoupon,
    show_random_products: showRandomProducts,
    home_message_variant: homeMessageVariant,
  }
  window.firebaseRemoteConfigData = data

  try {
    sessionStorage.setItem('home_message_variant', homeMessageVariant)
    localStorage.setItem('home_message_variant', homeMessageVariant)
  } catch {
    /* ignore */
  }

  if (
    window.RemoteConfigManager &&
    typeof window.RemoteConfigManager.applyRemoteData === 'function'
  ) {
    window.RemoteConfigManager.applyRemoteData(data, 'home-v2-firebase')
  }
}

export function installCompatFacade(): void {
  const firebaseCompat = {
    initializeApp(config?: Record<string, string>) {
      if (config && !window.firebaseConfig) {
        window.firebaseConfig = config
      }
      return getApp()
    },
    app: () => getApp(),
    firestore: makeFirestoreService(),
    analytics: makeAnalyticsService(),
    remoteConfig: makeRemoteConfigService(),
    INTERNAL: {},
  }

  Object.defineProperty(firebaseCompat, 'apps', {
    get() {
      try {
        return getApp() ? [getApp()] : []
      } catch {
        return []
      }
    },
  })

  ;(window as unknown as { firebase: typeof firebaseCompat }).firebase = firebaseCompat
  exposeBenjarukHomeFirebase()
}

export async function bootHomeV2Firebase(): Promise<void> {
  installCompatFacade()
  await getReady()
  await fetchHomeRemoteConfig()
  const ga = await getFirebaseAnalytics()
  if (window.BenjarukHomeFirebase) {
    window.BenjarukHomeFirebase.analytics = ga as Analytics | null
  }
  if (window.BenjarukFirebase) {
    window.BenjarukFirebase.analytics = ga as Analytics | null
  }
}
